import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { AbandonedTrip } from '@/api/entities';
import { FlightBooking } from '@/api/entities';
import { Passenger } from '@/api/entities';
import { User } from '@/api/entities';
import { StayBooking } from '@/api/entities';
import { Button } from '@/components/ui/button';
import SectionBand from '@/components/ui/SectionBand';
import { cn } from '@/lib/utils';
import {
  BRAND_NESTED_ROW,
  BRAND_PRIMARY_BTN_CLASS,
  CART_SECTION_CARD,
} from '@/constants/brandColors';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Send, Save, AlertCircle, Info, CheckCircle, Loader2, Plane, Calendar, Users, DollarSign, ExternalLink, Hotel, Clock, Gift, Trash2, User as UserIcon, Armchair, Check } from 'lucide-react';
import { getSeatPreferenceLabel } from '@/utils/seatPreference';
import PaymentScheduleDisplay from '../components/booking/PaymentScheduleDisplay';
import PaymentProcessorBadge from '@/components/defera-capital-nmi/PaymentProcessorBadge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { createPageUrl, getServiceFeeDisplayLabel } from '@/utils';
import { DEFERA_CAPITAL_HOLD_FEE_RATE, SLICE_PAY_HOLD_FEE_RATE } from '@/utils/paymentScheduleCalculator';
import { derivePricingForCart } from '@/utils/cartPricing';
import { readCartDiscount, applyDiscountToSubtotal } from '@/utils/discountCodes';
import { resolveHotelRating } from '@/utils/hotelRating';
import { useInstallmentsCopy } from '@/hooks/useInstallmentsCopy';
import { useInstallmentsCheckoutProvider } from '@/hooks/useInstallmentsCheckoutProvider';
import { getMarketingFareName, resolveMarketingFareNameFromOffer } from '@/utils/marketingFareName';
import { parseISO } from 'date-fns';
import { fmtAmount } from '@/utils/money';

/**
 * Render a stay's date field without letting `new Date('YYYY-MM-DD')` shift it
 * to the previous local day (the UTC-midnight → America/* midnight bug). Bare
 * `YYYY-MM-DD` values go through `parseISO` (local midnight); full ISO strings
 * with a time component are handed to `new Date` as-is so time zones ride
 * along.
 */
const formatStayDateLocal = (value) => {
  if (!value) return 'N/A';
  const raw = String(value);
  const bareYmd = /^\d{4}-\d{2}-\d{2}$/.test(raw);
  const parsed = bareYmd ? parseISO(raw) : new Date(raw);
  if (Number.isNaN(parsed.getTime())) return 'N/A';
  return parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const GHOST_BTN_CLASS =
  'rounded-full border border-white/15 bg-white/[0.03] text-white/70 ' +
  'hover:text-white hover:bg-white/[0.08] transition-colors duration-200';

/** Uniform royal shell + SectionBand header for every card on this page. */
function CartSection({ icon, title, subtitle, children, bodyClassName, delay = 0.15 }) {
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}>
      <div className={CART_SECTION_CARD}>
        <SectionBand icon={icon} title={title} subtitle={subtitle} />
        <div className={cn('px-4 py-5 sm:px-6 space-y-4', bodyClassName)}>{children}</div>
      </div>
    </motion.div>
  );
}

export default function EditCartPage() {
  const ic = useInstallmentsCopy();
  const installmentsCheckoutProvider = useInstallmentsCheckoutProvider();
  const isDeferaCapitalEffective = (pm) =>
    pm === 'defera_capital' ||
    ((pm === 'installments' || pm === 'miles_installments') && installmentsCheckoutProvider === 'defera_capital');
  const location = useLocation();
  const navigate = useNavigate();
  const [cart, setCart] = useState(null);
  const [user, setUser] = useState(null);
  const [stay, setStay] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState('');
  const [notification, setNotification] = useState(null);
  const pushInProgressRef = useRef(false);
  const [backers, setBackers] = useState([]);
  const [selectedBackerId, setSelectedBackerId] = useState('none');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const formatCabinDisplay = (rawValue) => {
    const value = String(rawValue || '').trim();
    if (!value) return null;
    if (value === 'premium_economy') return 'Premium Economy';
    return value
      .replaceAll('_', ' ')
      .split(' ')
      .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''))
      .join(' ');
  };

  const cartId = new URLSearchParams(location.search).get('id');

  const loadCart = useCallback(async () => {
    if (!cartId) {
      navigate(createPageUrl('Carts'));
      return;
    }
    setIsLoading(true);
    try {
      const currentUser = await User.me();
      if (currentUser.role !== 'admin') {
        navigate(createPageUrl('Dashboard'));
        return;
      }
      const cartData = await AbandonedTrip.get(cartId);
      console.log('Loaded cart data:', cartData);
      console.log('Cart passengers field:', cartData.passengers);
      console.log('Cart stay_data field:', cartData.stay_data);
      
      // Load all users and find the one matching this cart's email
      const allUsers = await User.list();
      let cartUser = allUsers.find(u => u.email === cartData.user_email);
      // Fallback: match by user_id if present on cart
      if (!cartUser && cartData.user_id) {
        cartUser = allUsers.find(u => u.id === cartData.user_id);
      }
      
      // Load backers for selection
      const backerUsers = allUsers.filter(u => {
        const roles = u.app_metadata?.roles || [];
        return roles.includes('backer');
      });
      setBackers(backerUsers);
      
      // FIXED: No need to fetch stay data separately - it's already included in cart.stay_data
      // AbandonedTrip.get() now fetches it automatically from stays table using session_id
      
      setCart(cartData);
      setStay(cartData.stay_data); // Use stay_data from cart
      // Preserve user_id when user lookup fails so push-to-trips can proceed
      setUser(cartUser || { id: cartData.user_id || null, email: cartData.user_email, firstName: '', lastName: '' });
      setStatus(cartData.status);
    } catch (error) {
      console.error('Error loading cart:', error);
      console.error('Error details:', error.message, error.stack);
      setNotification({ type: 'error', message: 'Failed to load cart details.' });
    } finally {
      setIsLoading(false);
    }
  }, [cartId, navigate]);

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const handleStatusChange = async () => {
    setIsProcessing(true);
    try {
      await AbandonedTrip.update(cart.id, { status });
      setNotification({ type: 'success', message: 'Status updated successfully.' });
      loadCart(); // Refresh data
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to update status.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteCart = async () => {
    if (!cart?.id) return;
    setIsDeleting(true);
    try {
      await AbandonedTrip.delete(cart.id);
      setShowDeleteConfirm(false);
      setNotification({ type: 'success', message: 'Cart deleted successfully.' });
      setTimeout(() => navigate(createPageUrl('Carts')), 800);
    } catch (error) {
      setNotification({ type: 'error', message: error.message || 'Failed to delete cart.' });
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const handlePushToTrips = async () => {
    // CRITICAL: Prevent duplicate submissions with ref guard
    if (pushInProgressRef.current) {
      console.warn('Push to trips already in progress, ignoring duplicate click');
      return;
    }
    pushInProgressRef.current = true;
    
    setIsProcessing(true);
    setNotification({ type: 'info', message: 'Converting cart to a booked trip... This may take a moment.' });
    try {
      const { flight_data, user_email, passengers: cartPassengers, seat_selections, total_amount } = cart;
      const stay_data = stay; // Use fetched stay data from stays table
      
      // Cart owner resolution:
      // Prefer persisted cart.user_id; otherwise fall back to resolved user from email (if present).
      // If still missing, backend will self-heal by resolving/creating an auth user by email.
      const cartOwnerId = cart.user_id || user?.id || null;
      
      // CRITICAL: Use existing booking_reference from Slice Pay if present
      // Check multiple locations where the reference might be stored
      const bookingReference = 
        cart.booking_reference || 
        cart.flight_data?.defera_booking_reference || 
        cart.flight_data?.booking_reference || 
        (cart.flight_data?.id ? cart.flight_data.id.slice(-6).toUpperCase() : `DEF-${Math.random().toString(36).substring(2, 8).toUpperCase()}`);
      
      console.log('🎫 Using booking reference:', bookingReference);
      
      // Enhance flight data with seat selections, passenger info, and preserve credits/pricing
      // CRITICAL: Include passengers from cart.passengers OR flight_data.passengers
      // Consolidate passenger extraction to a single source of truth
      let sourcePassengers = cartPassengers;
      if (!sourcePassengers || sourcePassengers.length === 0) {
        sourcePassengers = flight_data?.passengers || [];
      }
      
      // Ensure we have an array
      if (!Array.isArray(sourcePassengers)) {
        sourcePassengers = [];
      }

      const isFlightHotelPackage = !!(stay_data && parseFloat(stay_data.totalAmount || stay_data.total_amount || 0) > 0);
      const enhancedFlightData = {
        ...flight_data,
        seat_selections: seat_selections || {},
        passengers: sourcePassengers,
        defera_reference: bookingReference,
        confirmation_code: 'MANUAL_PUSH',
        // CRITICAL: Preserve credits and pricing_snapshot from cart
        credits: flight_data?.credits || [],
        credits_total: flight_data?.credits_total || 0,
        pricing_snapshot: flight_data?.pricing_snapshot || null,
        // Preserve payment method and miles for Cost Breakdown display
        _payment_method: flight_data?._payment_method ?? flight_data?.payment_method ?? cart.payment_method,
        _miles_to_deplete: flight_data?._miles_to_deplete ?? cart.miles_to_deplete ?? 0,
        // Preserve payment schedule data - vital for installment plans
        // CRITICAL: This deposit_percentage from cart must supersede calculator defaults
        payment_schedule: flight_data?.payment_schedule || null,
        // Flag for downstream: Accounting, Investor, Analytics use 0 service fee when true
        _is_flight_hotel_package: isFlightHotelPackage,
        // Preserve promo_applied for Trips/Carts/Investor labeling (Private Sale / Off-Market)
        promo_applied: flight_data?.promo_applied || false
      };
      
      // Log payment_schedule transfer for debugging
      if (enhancedFlightData.payment_schedule) {
        console.log('✅ Transferring payment_schedule from cart to booking:', {
          deposit_percentage: enhancedFlightData.payment_schedule.deposit_percentage,
          payment_plan: enhancedFlightData.payment_schedule.payment_plan,
          hasSchedule: !!enhancedFlightData.payment_schedule.schedule
        });
      } else {
        console.log('⚠️ Cart has no payment_schedule - will not be transferred to booking');
      }

      // PRIORITY: Use persisted pricing_snapshot if available (most accurate, credits-first applied)
      const pricingSnapshot = flight_data?.pricing_snapshot;
      const pushPaymentMethod = cart.payment_method || flight_data?._payment_method || flight_data?.payment_method || 'installments';
      const holdRateForPush = isDeferaCapitalEffective(pushPaymentMethod) ? DEFERA_CAPITAL_HOLD_FEE_RATE : SLICE_PAY_HOLD_FEE_RATE;
      let basePrice, serviceFee, grandTotal, holdFee, grandTotalWithHold;
      
      if (pricingSnapshot?.grand_total) {
        // Use persisted pricing from Review page (credits already applied correctly)
        basePrice = parseFloat(pricingSnapshot.original_base_fare || flight_data?.total_amount || 0);
        const stayPriceFromSnapshot = stay_data ? parseFloat(stay_data.totalAmount || stay_data.total_amount || 0) : 0;
        const isFlightHotelPackage = stayPriceFromSnapshot > 0;
        // Flight+hotel or promo: waive service fee (override snapshot which may be from before waiver)
        const promoApplied = !!flight_data?.promo_applied;
        serviceFee = (isFlightHotelPackage || promoApplied) ? 0 : parseFloat(pricingSnapshot.service_fee || 0);
        if (isFlightHotelPackage) {
          // original_base_fare in snapshot is already flight+hotel when it's a package
          const feeBase = Math.max(0, basePrice - (parseFloat(flight_data?.credits_total || 0)));
          const subtotal = feeBase + serviceFee;
          holdFee = subtotal * holdRateForPush;
          grandTotal = subtotal;
          grandTotalWithHold = subtotal + holdFee;
        } else {
          holdFee = parseFloat(pricingSnapshot.hold_fee || 0);
          grandTotalWithHold = parseFloat(pricingSnapshot.grand_total);
          grandTotal = grandTotalWithHold - holdFee;
        }
        console.log('📊 Using persisted pricing_snapshot:', { 
          basePrice, 
          serviceFee, 
          holdFee, 
          grandTotal, 
          grandTotalWithHold,
          creditsTotal: pricingSnapshot.credits_total || 0,
          isFlightHotelPackage
        });
      } else {
        // FALLBACK: Calculate pricing for older carts without snapshot - MUST apply credits-first rule
        const flightBasePrice = parseFloat(flight_data?.total_amount || 0);
        // Note: StayBooking class uses camelCase properties
        const stayPrice = stay_data ? parseFloat(stay_data.totalAmount || 0) : 0;
        const creditsTotal = parseFloat(flight_data?.credits_total || 0);
        const promoApplied = !!flight_data?.promo_applied;
        const { calculateServiceFee, calculateReviewPagePricing } = await import('../utils');
        const paymentMethod = pushPaymentMethod;
        const isMilesOnly = paymentMethod === 'miles';
        const isMilesInstallments = paymentMethod === 'miles_installments';
        const milesToDeplete = cart.miles_to_deplete ?? flight_data?._miles_to_deplete ?? 0;
        
        // CRITICAL: Credits-first rule - credits apply ONLY to flight base fare, not hotel
        const flightAfterCredits = Math.max(0, flightBasePrice - creditsTotal);
        const feeBase = flightAfterCredits + stayPrice; // Add hotel back for fee calculation
        basePrice = flightBasePrice; // Original flight base for record
        if (isMilesInstallments && milesToDeplete > 0) {
          const pricing = calculateReviewPagePricing(
            feeBase,
            0,
            paymentMethod,
            milesToDeplete,
            0,
            stayPrice > 0 || promoApplied,
            isDeferaCapitalEffective(paymentMethod) ? 'defera_capital' : 'slice_pay'
          );
          serviceFee = pricing.serviceFee;
          holdFee = pricing.holdFee;
          grandTotal = pricing.subtotalBeforeHold;
          grandTotalWithHold = pricing.totalExcludingSeats;
        } else if (paymentMethod === 'reserve') {
          serviceFee = stayPrice > 0 ? 0 : feeBase * 0.11;
          grandTotal = feeBase + serviceFee;
          holdFee = 0;
          grandTotalWithHold = grandTotal;
        } else if (paymentMethod === 'defera_zero') {
          const pricing = calculateReviewPagePricing(feeBase, 0, 'defera_zero', 0, 0, stayPrice > 0 || promoApplied);
          serviceFee = pricing.serviceFee;
          holdFee = pricing.holdFee;
          grandTotal = pricing.subtotalBeforeHold;
          grandTotalWithHold = pricing.totalExcludingSeats;
        } else {
          // Service fee WAIVED for flight+hotel or promo; otherwise calculated on reduced flight + hotel
          serviceFee = (isMilesOnly || stayPrice > 0 || promoApplied) ? 0 : calculateServiceFee(feeBase, isDeferaCapitalEffective(paymentMethod));
          grandTotal = feeBase + serviceFee;
          holdFee = isMilesOnly ? 0 : (grandTotal * holdRateForPush);
          grandTotalWithHold = grandTotal + holdFee;
        }
        
        console.log('📊 Calculated pricing (no snapshot, credits-first applied):', { 
          flightBasePrice, 
          stayPrice,
          creditsTotal, 
          flightAfterCredits, 
          feeBase,
          serviceFee, 
          holdFee, 
          grandTotal,
          grandTotalWithHold
        });
      }

      // Discount code (migration 141) — applied last, to whichever of the two
      // branches above produced the numbers. This is the figure that gets
      // PERSISTED to the booking row, so it has to match what the customer was
      // actually charged at checkout, not the pre-discount list price.
      //
      // The pre-hold total is reduced by the grossed-down amount and the hold
      // recomputed on it, exactly as Review did — so grand_total, hold_fee and
      // grand_total_with_hold stay internally consistent instead of only the
      // headline being right. The effective rate is derived from what this cart
      // actually produced, which lands on 0 for Reserve / miles-only.
      const pushDiscount = readCartDiscount(flight_data);
      if (pushDiscount) {
        const effectiveHoldRate = grandTotal > 0 ? holdFee / grandTotal : 0;
        const { subtotalAfterDiscount, grandTotalDiscount } = applyDiscountToSubtotal(
          grandTotal, pushDiscount.cents, effectiveHoldRate
        );
        grandTotal = subtotalAfterDiscount;
        holdFee = Math.round(subtotalAfterDiscount * effectiveHoldRate * 100) / 100;
        grandTotalWithHold = Math.round((grandTotal + holdFee) * 100) / 100;
        console.log('🏷️ Discount code applied to push:', {
          code: pushDiscount.code,
          customerSaves: grandTotalDiscount,
          grandTotal,
          holdFee,
          grandTotalWithHold,
        });
      }

      // Get session for API calls
      const { supabase } = await import('../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // CRITICAL: Use existing stay from stays table (don't create duplicate)
      // The stay was already saved with session_id when user selected hotel
      let stayId = stay_data?.id || null;
      let staySnapshot = stay_data || null;

      // Create a single booking record with all flight information
      // IMPORTANT: Use cart's user_id so the booking belongs to the cart owner, not the admin
      const bookingData = {
        user_id: cartOwnerId,
        user_email: cart.user_email, // for backend self-heal when user_id is missing
        cart_id: cart.id, // for backend backfill of abandoned_trips.user_id
        offer_id: cart.offer_id || bookingReference,
        booking_reference: bookingReference,
        status: 'confirmed',
        total_amount: basePrice,
        service_fee: serviceFee,
        grand_total: grandTotal,
        hold_fee: holdFee,
        grand_total_with_hold: grandTotalWithHold,
        total_currency: 'USD',
        passenger_count: (cartPassengers || flight_data?.passengers || [])?.length || 1,
        // I74BF5: write the airline's actual fare brand (e.g. "Level") onto
        // bookings.cabin_class instead of the search-form enum that
        // `transformDuffelOfferFromSerpAPI` parks on `flight_data.cabin_class`.
        // Otherwise admin-pushed bookings inherit the same column-vs-flight_data
        // mismatch as Defera Capital / Reserve and trips render generic "Economy".
        cabin_class: resolveMarketingFareNameFromOffer(
          enhancedFlightData,
          flight_data.cabin_class || '',
        ),
        flight_data: enhancedFlightData,
        payment_url: cart.payment_link || cart.slicepay || null,
        backer_id: selectedBackerId && selectedBackerId !== 'none' ? selectedBackerId : null
      };
      
      // Use admin API endpoint to bypass RLS
      const response = await fetch('/api/admin/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        credentials: 'omit',
        body: JSON.stringify(bookingData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create booking');
      }
      
      const { data: newBooking } = await response.json();

      // CRITICAL: Link existing stay to booking using admin endpoint (bypasses RLS)
      if (stayId) {
        const linkResponse = await fetch(`/api/admin/stays/${stayId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          credentials: 'omit',
          body: JSON.stringify({ booking_id: newBooking.id })
        });

        if (!linkResponse.ok) {
          console.error('Failed to link stay to booking');
          throw new Error('Failed to link hotel to booking');
        } else {
          console.log('Successfully linked stay (ID:', stayId, ') to booking (ID:', newBooking.id, ')');
        }
      }

      // CRITICAL: Check multiple sources for passenger data
      // Passengers might be in cart.passengers OR cart.flight_data.passengers
      let allPassengers = cartPassengers || [];
      if ((!allPassengers || allPassengers.length === 0) && flight_data?.passengers) {
        console.warn('⚠️ No passengers in cart.passengers, checking flight_data.passengers');
        allPassengers = flight_data.passengers;
      }
      
      // Also check enhancedFlightData.passengers as a final fallback
      if ((!allPassengers || allPassengers.length === 0) && enhancedFlightData?.passengers) {
        console.warn('⚠️ No passengers found, checking enhancedFlightData.passengers');
        allPassengers = enhancedFlightData.passengers;
      }
      
      console.log('📋 Final passenger data to transfer:', {
        source: cartPassengers ? 'cart.passengers' : (flight_data?.passengers ? 'flight_data.passengers' : 'enhancedFlightData.passengers'),
        count: allPassengers?.length || 0,
        passengers: allPassengers
      });

      // Save passengers if they exist - USE ADMIN API TO BYPASS RLS
      if (allPassengers && allPassengers.length > 0) {
        console.log('About to save', allPassengers.length, 'passengers for booking ID:', newBooking.id);
        console.log('Passenger data from cart:', JSON.stringify(allPassengers, null, 2));
        
        // Smart-strict date parser for PostgreSQL date columns.
        //
        // Background (incident VO1SVD, May 2026): the previous version fell back
        // to `new Date(str)` which silently parses slashed strings using the
        // host's locale. For UK customers entering `DD/MM/YYYY` that meant
        // either `null` (day > 12) or a *wrong* date stored as if it were
        // `MM/DD`. We only caught it when a CS rep noticed missing DOBs.
        //
        // Now we accept:
        //   - ISO `YYYY-MM-DD` (and full timestamps)
        //   - 8-digit numeric (legacy MMDDYYYY / DDMMYYYY)
        //   - Slashed strings only when one side > 12 (unambiguously DD/MM or MM/DD)
        // Genuinely ambiguous slashed dates (both sides 1..12) are *rejected*
        // rather than silently guessed.
        const convertDate = (dateStr) => {
          if (!dateStr || (typeof dateStr === 'string' && dateStr.trim() === '')) return null;
          
          try {
            const str = String(dateStr).trim();
            
            if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
              return str;
            }
            if (/^\d{4}-\d{2}-\d{2}T/.test(str)) {
              return str.slice(0, 10);
            }
            
            if (/^\d{8}$/.test(str)) {
              const firstTwo = parseInt(str.substring(0, 2), 10);
              const secondTwo = parseInt(str.substring(2, 4), 10);
              const yearStr = str.substring(4, 8);
              const yearNum = parseInt(yearStr, 10);
              
              if (yearNum < 1900 || yearNum > 2100) {
                console.warn('Invalid year in 8-digit date:', dateStr);
                return null;
              }
              
              if (firstTwo >= 1 && firstTwo <= 12) {
                const month = str.substring(0, 2);
                const day = str.substring(2, 4);
                const dayNum = parseInt(day, 10);
                
                if (dayNum >= 1 && dayNum <= 31) {
                  return `${yearStr}-${month}-${day}`;
                }
              }
              
              if (firstTwo > 12 && secondTwo >= 1 && secondTwo <= 12) {
                const day = str.substring(0, 2);
                const month = str.substring(2, 4);
                const dayNum = parseInt(day, 10);
                
                if (dayNum >= 1 && dayNum <= 31) {
                  return `${yearStr}-${month}-${day}`;
                }
              }
              
              console.warn('Invalid 8-digit date format - cannot determine MMDDYYYY or DDMMYYYY:', dateStr);
              return null;
            }
            
            const slashed = str.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{2}|\d{4})$/);
            if (slashed) {
              const a = parseInt(slashed[1], 10);
              const b = parseInt(slashed[2], 10);
              const y = slashed[3].length === 4
                ? slashed[3]
                : (parseInt(slashed[3], 10) > 30 ? `19${slashed[3]}` : `20${slashed[3]}`);
              const yN = parseInt(y, 10);
              if (yN < 1900 || yN > 2100) return null;
              if (a > 12 && b >= 1 && b <= 12) {
                return `${y}-${String(b).padStart(2, '0')}-${String(a).padStart(2, '0')}`;
              }
              if (b > 12 && a >= 1 && a <= 12) {
                return `${y}-${String(a).padStart(2, '0')}-${String(b).padStart(2, '0')}`;
              }
              console.warn('Rejected ambiguous slashed passenger date — could be DD/MM or MM/DD:', dateStr);
              return null;
            }
            
            console.warn('Rejected unrecognised passenger date format:', dateStr);
            return null;
          } catch (e) {
            console.warn('Error parsing date:', dateStr, e);
            return null;
          }
        };

        const passengersData = allPassengers.map((passenger, idx) => {
          // Log original passenger data for debugging
          console.log(`📋 Passenger ${idx + 1} original data:`, JSON.stringify(passenger, null, 2));
          
          // Handle all possible field name variations from frontend
          const firstName = passenger.first_name || passenger.given_name || passenger.givenName || passenger.firstName || '';
          const lastName = passenger.last_name || passenger.family_name || passenger.familyName || passenger.lastName || '';
          
          // CRITICAL: Check ALL possible DOB field name variations (including nested)
          // Also check if DOB might be in a nested object or array
          let dobRaw = passenger.date_of_birth || passenger.born_on || passenger.bornOn || passenger.dateOfBirth || 
                       passenger.dob || passenger.DOB || 
                       passenger.dateOfBirth || passenger.DateOfBirth || 
                       null;
          
          // If still not found, check for any field that might contain a date
          if (!dobRaw) {
            // Check all keys for date-like patterns
            const allKeys = Object.keys(passenger || {});
            for (const key of allKeys) {
              const value = passenger[key];
              if (value && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
                // Might be a date field we haven't checked
                if (key.toLowerCase().includes('birth') || key.toLowerCase().includes('dob') || key.toLowerCase().includes('born')) {
                  dobRaw = value;
                  console.log(`🔍 Found DOB in unexpected field "${key}":`, dobRaw);
                  break;
                }
              }
            }
          }
          
          const dob = convertDate(dobRaw);
          
          // Log DOB extraction for debugging - MORE VERBOSE
          if (!dob && dobRaw) {
            console.error(`❌ Passenger ${idx + 1} DOB found but failed to convert:`, dobRaw, typeof dobRaw);
            console.error(`   Passenger object:`, JSON.stringify(passenger, null, 2));
          } else if (!dob && !dobRaw) {
            console.error(`❌ Passenger ${idx + 1} has NO DOB field found in passenger object`);
            console.error(`   Available passenger keys:`, Object.keys(passenger));
            console.error(`   Full passenger object:`, JSON.stringify(passenger, null, 2));
          } else {
            console.log(`✅ Passenger ${idx + 1} DOB successfully extracted:`, dob, '(from raw:', dobRaw, ')');
          }
          
          const phone = passenger.phone || passenger.phone_number || passenger.phoneNumber || '';
          const passportExpiry = convertDate(passenger.passport_expiry || passenger.passport_expiry_date || passenger.passportExpiryDate || passenger.passportExpiry);
          
          // CRITICAL: Transfer ALL passenger fields - check for any additional fields that might exist
          // Send ONLY database-compatible field names (no duplicates)
          // CRITICAL: Send both born_on (converted) AND date_of_birth (raw) as fallback
          // Backend API checks: born_on || date_of_birth, so if conversion fails, backend can use raw value
          const passengerData = {
            user_id: cartOwnerId,
            booking_id: newBooking.id,
            type: passenger.type || 'adult',
            title: passenger.title || '',
            given_name: firstName,
            middle_name: passenger.middle_name || passenger.middleName || '',
            family_name: lastName,
            gender: passenger.gender || '',
            born_on: dob,  // Converted DOB (YYYY-MM-DD format)
            date_of_birth: dob || dobRaw || null,  // Fallback: converted DOB, or raw value if conversion failed
            email: passenger.email || '',
            phone_number: phone,
            passport_number: passenger.passport_number || passenger.passportNumber || passenger.passport || '',
            passport_expiry_date: passportExpiry,
            passport_country: passenger.passport_country || passenger.passportCountry || '',
            nationality: passenger.nationality || '',
            frequent_flyer_number: passenger.frequent_flyer_number || passenger.frequentFlyerNumber || '',
            known_traveler_number: passenger.known_traveler_number || passenger.knownTravelerNumber || '',
            special_requests: passenger.special_requests || passenger.specialRequests || '',
            seat_preference: passenger.seat_preference || passenger.seatPreference || ''
          };
          
          // Log any fields from original passenger that weren't mapped (for debugging)
          const originalKeys = Object.keys(passenger);
          const mappedKeys = Object.keys(passengerData);
          const unmappedKeys = originalKeys.filter(key => 
            !mappedKeys.includes(key) && 
            !['first_name', 'given_name', 'givenName', 'firstName', 
              'middle_name', 'middleName',
              'last_name', 'family_name', 'familyName', 'lastName',
              'date_of_birth', 'born_on', 'bornOn', 'dateOfBirth',
              'phone', 'phone_number', 'phoneNumber',
              'passport_expiry', 'passport_expiry_date', 'passportExpiryDate', 'passportExpiry',
              'passport_number', 'passportNumber', 'passport',
              'passport_country', 'passportCountry',
              'frequent_flyer_number', 'frequentFlyerNumber',
              'known_traveler_number', 'knownTravelerNumber',
              'special_requests', 'specialRequests',
              'seat_preference', 'seatPreference'].includes(key)
          );
          
          if (unmappedKeys.length > 0) {
            console.warn(`⚠️ Passenger ${idx + 1} has unmapped fields:`, unmappedKeys);
            console.warn(`   Unmapped values:`, unmappedKeys.map(k => ({ [k]: passenger[k] })));
          }
          
          console.log(`✅ Passenger ${idx + 1} mapped data:`, passengerData);
          return passengerData;
        });

        const passengersResponse = await fetch('/api/admin/passengers', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`
          },
          credentials: 'omit',
          body: JSON.stringify(passengersData)
        });

        if (!passengersResponse.ok) {
          const errorData = await passengersResponse.json();
          console.error('Failed to save passengers:', errorData.error);
          throw new Error(`Failed to save passengers: ${errorData.error}`);
        }

        const { data: savedPassengers } = await passengersResponse.json();
        console.log('Successfully saved', savedPassengers.length, 'passengers');
      } else {
        console.log('No passengers in cart to save');
      }

      // Update the cart status to confirmed
      await AbandonedTrip.update(cart.id, {
        status: 'confirmed'
      });

      // Send trip confirmation email to user
      try {
        // Get user email from multiple sources (cart.user_email, user.email, or session)
        const recipientEmail = user_email || user?.email || cart.user_email;
        
        if (!recipientEmail) {
          console.warn('⚠️ Cannot send trip confirmation email: no email address found', {
            user_email,
            userEmail: user?.email,
            cartUserEmail: cart.user_email
          });
        } else {
          // Get user name from passengers or user object
          const firstName = allPassengers?.[0]?.first_name || 
                           allPassengers?.[0]?.given_name || 
                           allPassengers?.[0]?.givenName ||
                           user?.firstName || 
                           user?.first_name ||
                           recipientEmail.split('@')[0];
          
          console.log('📧 Sending trip confirmation email', {
            email: recipientEmail,
            userName: firstName,
            hasPassengers: !!allPassengers,
            passengerCount: allPassengers?.length || 0
          });

        const emailResponse = await fetch('/api/send-trip-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
              userEmail: recipientEmail,
              userName: firstName
          })
        });

        if (emailResponse.ok) {
            const emailData = await emailResponse.json();
            console.log('✅ Trip confirmation email sent successfully', {
              messageId: emailData.messageId,
              email: recipientEmail
            });
        } else {
            const errorData = await emailResponse.json().catch(() => ({ error: 'Unknown error' }));
            console.error('❌ Failed to send trip confirmation email', {
              status: emailResponse.status,
              error: errorData.error || errorData.details,
              email: recipientEmail
            });
          }
        }
      } catch (emailError) {
        console.error('❌ Error sending trip confirmation email:', emailError);
        console.error('Error details:', {
          message: emailError.message,
          stack: emailError.stack
        });
      }

      setNotification({ type: 'success', message: 'Successfully converted cart to a trip!' });
      setTimeout(() => navigate(createPageUrl('Dashboard')), 2000);

    } catch (error) {
      console.error("Push to trips failed:", error);
      console.error("Error details:", error.message, error.stack);
      setNotification({ type: 'error', message: `Failed to convert: ${error.message || 'Unknown error'}` });
    } finally {
      // CRITICAL: Always reset guards to allow future submissions
      setIsProcessing(false);
      pushInProgressRef.current = false;
    }
  };

  if (isLoading) {
    return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-white"/></div>;
  }
  
  if (!cart) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center text-center gap-2">
        <AlertCircle className="w-8 h-8 text-rose-400 mb-1"/>
        <h2 className="text-xl font-light tracking-tight text-white">Cart Not Found</h2>
        <p className="text-white/60 font-normal text-sm">The requested cart could not be found.</p>
        <Button onClick={() => navigate(createPageUrl('Carts'))} className={cn(BRAND_PRIMARY_BTN_CLASS, 'mt-3 h-10 px-6')}>Back to Carts</Button>
      </div>
    );
  }
  
  const NotifIcon = notification ? {
    success: CheckCircle, info: Info, error: AlertCircle
  }[notification.type] : null;

  // Single source of truth for the Price Summary card. Reads the pricing
  // snapshot persisted by Review.jsx (i.e. exactly what the customer saw at
  // checkout) and falls back to calculateReviewPagePricing for legacy carts
  // that pre-date the snapshot field. Push-to-trips below has its own block
  // and is intentionally left untouched — `derivePricingForCart` is for
  // *display* only.
  const pricing = derivePricingForCart(cart, stay, { isDeferaCapitalEffective });
  // Duffel scores out of 10 and Google out of 5: print it on its own scale.
  const stayRating = resolveHotelRating(stay);
  const {
    paymentMethod,
    isMilesOnly,
    isMilesInstallments,
    isFlightHotel,
    promoApplied,
    discountCode,
    discountApplied,
    hasCredits,
    originalFlightBase: basePrice,
    creditsTotal,
    feeBase: basePriceForFees,
    stayTotal,
    milesToDeplete,
    serviceFee,
    holdFee,
    subtotalBeforeHold: subtotal,
    cashPortion: cashPortionDisplay,
    milesUsed: milesUsedDisplay,
    seatTotal,
    grandTotalIncludingSeats: finalTotal,
  } = pricing;
  // Seats are financed (inside the installment schedule), so the schedule
  // covers the full trip total — not the seat-free variant.
  const totalForPaymentSchedule = finalTotal;

  return (
    <div className="min-h-[70vh]">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 sm:gap-4 mb-6">
            <Button variant="ghost" size="icon" onClick={() => navigate(createPageUrl('Carts'))} className="text-white/50 hover:text-white hover:bg-white/10 rounded-full flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-light tracking-tight text-white">Abandoned Cart</h2>
                <span className={cn(
                  'px-2.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider border',
                  cart.status === 'confirmed'
                    ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/25'
                    : cart.status === 'canceled'
                      ? 'bg-rose-500/10 text-rose-300 border-rose-500/25'
                      : 'bg-amber-500/10 text-amber-300 border-amber-500/25',
                )}>
                  {String(cart.status || '').replaceAll('_', ' ')}
                </span>
              </div>
              <p className="text-white/60 text-sm font-normal truncate">Cart details · {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete cart"
              className={cn(GHOST_BTN_CLASS, 'flex-shrink-0 hover:text-rose-300 hover:border-rose-500/30')}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <AlertDialogContent className="bg-brand-950/95 border-brand-800/45 backdrop-blur-xl text-white">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-white">Delete this cart?</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-300">
                  This will permanently remove this abandoned trip. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="bg-white/10 border-white/20 text-white hover:bg-white/20">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); handleDeleteCart(); }}
                  disabled={isDeleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </motion.div>
        
        {notification && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={cn(
              'p-3 rounded-xl mb-6 flex items-center gap-3 border',
              notification.type === 'success' && 'bg-emerald-500/[0.06] border-emerald-500/25',
              notification.type === 'error' && 'bg-rose-500/[0.06] border-rose-500/25',
              notification.type === 'info' && 'bg-brand-500/[0.06] border-brand-500/25',
            )}
          >
            <NotifIcon className={cn(
              'w-5 h-5 flex-shrink-0',
              notification.type === 'success' && 'text-emerald-300',
              notification.type === 'error' && 'text-rose-300',
              notification.type === 'info' && 'text-brand-300',
            )}/>
            <p className="text-sm text-white/90 font-normal">{notification.message}</p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Flight Details - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            <CartSection icon={Plane} title="Flight Details" delay={0.1} bodyClassName="space-y-6">
                  {/* Flight Offer ID */}
                  {cart.flight_data.id && (
                    <div className="pb-4 border-b border-white/[0.06]">
                      <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Offer ID</p>
                      <p className="text-sm text-white/85 font-mono font-normal break-all">{cart.flight_data.id}</p>
                    </div>
                  )}

                  {/* Slices (Outbound/Return) */}
                  {cart.flight_data.slices.map((slice, sliceIndex) => (
                    <div key={sliceIndex} className="space-y-3">
                      <div className="flex items-center gap-2.5 mb-1">
                        <span className="px-2.5 py-0.5 rounded-full text-[10px] font-normal uppercase tracking-wider bg-brand-500/15 text-brand-300 border border-brand-500/25">
                          {sliceIndex === 0 ? 'Outbound' : 'Return'}
                        </span>
                        {slice.duration && (
                          <span className="text-xs text-white/40 font-normal">Total duration {slice.duration}</span>
                        )}
                      </div>

                      {/* Segments */}
                      {slice.segments && slice.segments.map((segment, segIndex) => (
                        <div key={segIndex} className={cn(BRAND_NESTED_ROW, 'px-3 py-3 md:px-4 md:py-4 space-y-3')}>
                          {/* Segment Header */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2.5">
                              {segment.marketing_carrier?.logo_symbol_url ? (
                                <img 
                                  src={segment.marketing_carrier.logo_symbol_url}
                                  alt={segment.marketing_carrier.name}
                                  className="w-8 h-8 object-contain"
                                />
                              ) : null}
                              <div>
                                <p className="text-sm text-white font-medium">
                                  {segment.marketing_carrier?.name || 'Unknown Airline'} {segment.marketing_carrier_flight_number || ''}
                                </p>
                                <p className="text-xs text-white/40 font-normal">
                                  {segment.aircraft?.name || 'Unknown Aircraft'}
                                </p>
                              </div>
                            </div>
                            {segment.duration && (
                              <span className="text-xs text-white/40 font-normal">{segment.duration}</span>
                            )}
                          </div>

                          {/* Route Details */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Departure */}
                            <div>
                              <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Departure</p>
                              <p className="text-sm text-white font-medium">{segment.origin?.iata_code || 'N/A'}</p>
                              <p className="text-xs text-white/45 font-normal truncate">{segment.origin?.name || segment.origin?.city_name || 'Unknown Airport'}</p>
                              {segment.departing_at && (
                                <p className="text-xs text-white/85 mt-1">
                                  {new Date(segment.departing_at).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>

                            {/* Arrival */}
                            <div>
                              <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Arrival</p>
                              <p className="text-sm text-white font-medium">{segment.destination?.iata_code || 'N/A'}</p>
                              <p className="text-xs text-white/45 font-normal truncate">{segment.destination?.name || segment.destination?.city_name || 'Unknown Airport'}</p>
                              {segment.arriving_at && (
                                <p className="text-xs text-white/85 mt-1">
                                  {new Date(segment.arriving_at).toLocaleString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Additional Segment Info */}
                          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-white/[0.06] text-xs">
                            {segment.distance && (
                              <div>
                                <span className="text-white/40">Distance: </span>
                                <span className="text-white/80">{segment.distance}</span>
                              </div>
                            )}
                            {segment.operating_carrier && (
                              <div>
                                <span className="text-white/40">Operated by: </span>
                                <span className="text-white/80">{segment.operating_carrier.name || segment.operating_carrier.iata_code}</span>
                              </div>
                            )}
                            {segment.id && (
                              <div className="col-span-2">
                                <span className="text-white/40">Segment ID: </span>
                                <span className="text-white/40 font-mono text-[10px]">{segment.id}</span>
                              </div>
                            )}
                          </div>

                          {/* Layover Notice */}
                          {segment.layover_duration && segIndex < slice.segments.length - 1 && (
                            <div className="flex items-center gap-2 text-xs text-brand-300 bg-brand-500/10 border border-brand-500/20 rounded-lg px-2.5 py-1.5">
                              <Clock className="w-3 h-3" />
                              <span>Layover: {segment.layover_duration}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Cabin Class & Pricing */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/[0.06]">
                    {(() => {
                      const flightData = cart.flight_data;
                      const firstSegment = flightData?.slices?.[0]?.segments?.[0];
                      // _searchParams.cabin_class = what user searched (most reliable for carts)
                      const searchCabin = flightData?._searchParams?.cabin_class;
                      const marketingName = getMarketingFareName({
                        flight_data: flightData,
                        cabinClass: searchCabin || flightData?.cabin_class,
                        cabin_class: searchCabin || flightData?.cabin_class,
                      });
                      const cabinClass = formatCabinDisplay(
                        marketingName ||
                        firstSegment?.passengers_cabin_class_marketing_name ||
                        firstSegment?.cabin_class_marketing_name ||
                        searchCabin ||
                        flightData?.cabin_class ||
                        firstSegment?.cabin_class
                      );
                      return cabinClass ? (
                      <div>
                        <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Cabin Class</p>
                          <p className="text-sm text-white/90">{cabinClass}</p>
                      </div>
                      ) : null;
                    })()}
                    {cart.flight_data.total_amount && (
                      <div>
                        <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Base Fare</p>
                        <p className="text-sm text-white/90 tabular-nums">${fmtAmount(parseFloat(cart.flight_data.total_amount))}</p>
                      </div>
                    )}
                  </div>
            </CartSection>

            {/* Hotel/Stay Details */}
            {stay && (
              <CartSection icon={Hotel} title="Hotel Details" delay={0.15}>
                      {/* Hotel Header */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white text-sm font-medium mb-1">{stay.name}</h3>
                          {stay.address && (
                            <p className="text-white/45 text-xs font-normal mb-2">{stay.address}</p>
                          )}
                          {stayRating && (
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-white/40">Rating:</span>
                              <span className="text-white/85">{stayRating.label}</span>
                              {stay.reviews > 0 && (
                                <span className="text-white/40">({stay.reviews} reviews)</span>
                              )}
                            </div>
                          )}
                        </div>
                        {stay.hotelClass && (
                          <span className="flex-shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-normal uppercase tracking-wider bg-brand-500/15 text-brand-300 border border-brand-500/25">
                            {stay.hotelClass}-Star
                          </span>
                        )}
                      </div>

                      {/* Room & Booking Details */}
                      <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/[0.06]">
                        {stay.roomType && (
                          <div>
                            <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Room Type</p>
                            <p className="text-sm text-white/90 capitalize">{stay.roomType}</p>
                          </div>
                        )}
                        {stay.bedType && (
                          <div>
                            <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Bed Type</p>
                            <p className="text-sm text-white/90 capitalize">{stay.bedType}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Check-in</p>
                          <p className="text-sm text-white/90">
                            {formatStayDateLocal(stay.checkInDate)}
                          </p>
                        </div>
                        <div>
                          <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Check-out</p>
                          <p className="text-sm text-white/90">
                            {formatStayDateLocal(stay.checkOutDate)}
                          </p>
                        </div>
                        {stay.nights && (
                          <div>
                            <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Nights</p>
                            <p className="text-sm text-white/90">{stay.nights}</p>
                          </div>
                        )}
                        {stay.nightlyRate && (
                          <div>
                            <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Nightly Rate</p>
                            <p className="text-sm text-white/90 tabular-nums">${fmtAmount(parseFloat(stay.nightlyRate))}</p>
                          </div>
                        )}
                      </div>

                      {/* Amenities */}
                      {stay.amenities && stay.amenities.length > 0 && (
                        <div className="pt-3 border-t border-white/[0.06]">
                          <p className="text-[11px] text-white/40 uppercase tracking-wider mb-2">Amenities</p>
                          <div className="flex flex-wrap gap-1.5">
                            {stay.amenities.slice(0, 6).map((amenity, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 rounded-full bg-brand-900/30 text-white/70 border border-brand-800/25 text-xs"
                              >
                                {amenity}
                              </span>
                            ))}
                            {stay.amenities.length > 6 && (
                              <span className="px-2 py-1 rounded-full bg-brand-900/30 text-white/70 border border-brand-800/25 text-xs">
                                +{stay.amenities.length - 6} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Pricing */}
                      <div className="pt-3 border-t border-white/[0.06]">
                        <div className="flex justify-between items-center">
                          <p className="text-white/50 text-xs">Total Cost</p>
                          <p className="text-white text-sm font-medium tabular-nums">${fmtAmount(parseFloat(stay.totalAmount || 0))}</p>
                        </div>
                      </div>
              </CartSection>
            )}

            {/* Applied Credits */}
            {cart.flight_data?.credits && cart.flight_data.credits.length > 0 && (
              <CartSection icon={Gift} title={`Applied Credits (${cart.flight_data.credits.length})`} delay={0.2} bodyClassName="space-y-3">
                    {cart.flight_data.credits.map((credit, index) => (
                      <div key={index} className={cn(BRAND_NESTED_ROW, 'flex items-center justify-between p-3')}>
                        <div>
                          <p className="text-white text-sm font-medium">{credit.airlineName} {credit.creditTypeLabel}</p>
                          <p className="text-white/40 text-xs font-normal">Ref: {credit.reference?.slice(-6) || 'N/A'}{credit.claimantLastName && ` · ${credit.claimantLastName}`}</p>
                        </div>
                        <div className="text-emerald-300 font-medium tabular-nums">-${fmtAmount(credit.claimedAmount)}</div>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-3 border-t border-white/[0.06]">
                      <p className="text-white/50 text-sm font-normal">Total Credits Applied</p>
                      <p className="text-emerald-300 font-medium tabular-nums">-${fmtAmount(cart.flight_data.credits_total || cart.flight_data.credits.reduce((sum, c) => sum + (c.claimedAmount || 0), 0))}</p>
                    </div>
                    {cart.flight_data.pricing_snapshot && (
                      <div className="space-y-1 pt-2 text-xs text-white/45">
                        <div className="flex justify-between">
                          <span>Original Base Fare:</span>
                          <span className="tabular-nums">${fmtAmount(cart.flight_data.pricing_snapshot.original_base_fare)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>After Credits:</span>
                          <span className="tabular-nums">${fmtAmount(cart.flight_data.pricing_snapshot.base_fare_after_credits)}</span>
                        </div>
                        <div className="flex justify-between text-white/90">
                          <span>Final Total:</span>
                          <span className="tabular-nums">${fmtAmount(cart.flight_data.pricing_snapshot.grand_total)}</span>
                        </div>
                      </div>
                    )}
              </CartSection>
            )}

            {/* Seat Selections */}
            {cart.seat_selections && Object.keys(cart.seat_selections).length > 0 && (
              <CartSection icon={Armchair} title="Selected Seats" delay={0.2} bodyClassName="space-y-2">
                      {Object.entries(cart.seat_selections).map(([segmentKey, seats]) => (
                        <div key={segmentKey} className="text-sm">
                          <p className="text-white/40 text-xs mb-1.5">{segmentKey}:</p>
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(seats || {}).map(([seatId, seat]) => (
                              <span key={seatId} className="px-2.5 py-1 rounded-full text-xs text-white/85 bg-brand-900/35 border border-brand-800/30 tabular-nums">
                                {seatId} · ${typeof seat.price === 'object' ? seat.price?.amount : seat.price}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
              </CartSection>
            )}

            {/* Passenger Details */}
            {cart.passengers && cart.passengers.length > 0 && (
              <CartSection icon={Users} title={`Passengers (${cart.passengers.length})`} delay={0.25}>
                    {cart.passengers.map((passenger, index) => (
                      <div key={index} className={cn(BRAND_NESTED_ROW, 'p-3.5 space-y-2.5')}>
                        <div className="flex items-center gap-2">
                          <UserIcon className="w-3.5 h-3.5 text-brand-300" />
                          <span className="text-white font-medium text-sm">
                            {[passenger.title, passenger.first_name || passenger.firstName, passenger.middle_name || passenger.middleName, passenger.last_name || passenger.lastName].filter(Boolean).join(' ')}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          {passenger.email && (
                            <div>
                              <span className="text-white/40">Email:</span>
                              <p className="text-white/85 font-normal truncate">{passenger.email}</p>
                            </div>
                          )}
                          {passenger.phone && (
                            <div>
                              <span className="text-white/40">Phone:</span>
                              <p className="text-white/85 font-normal">{passenger.phone}</p>
                            </div>
                          )}
                          {passenger.date_of_birth && (
                            <div>
                              <span className="text-white/40">DOB:</span>
                              <p className="text-white/85 font-normal">{passenger.date_of_birth}</p>
                            </div>
                          )}
                          {passenger.gender && (
                            <div>
                              <span className="text-white/40">Gender:</span>
                              <p className="text-white/85 font-normal capitalize">{passenger.gender}</p>
                            </div>
                          )}
                          {passenger.passport_number && (
                            <div>
                              <span className="text-white/40">Passport:</span>
                              <p className="text-white/85 font-normal">{passenger.passport_number}</p>
                            </div>
                          )}
                          {passenger.passport_expiry && (
                            <div>
                              <span className="text-white/40">Passport Exp:</span>
                              <p className="text-white/85 font-normal">{passenger.passport_expiry}</p>
                            </div>
                          )}
                          {passenger.nationality && (
                            <div>
                              <span className="text-white/40">Nationality:</span>
                              <p className="text-white/85 font-normal">{passenger.nationality}</p>
                            </div>
                          )}
                          {passenger.frequent_flyer_program && (
                            <div>
                              <span className="text-white/40">FF Program:</span>
                              <p className="text-white/85 font-normal text-xs">{passenger.frequent_flyer_program.replace('_', ' ')}</p>
                            </div>
                          )}
                          {passenger.frequent_flyer_number && (
                            <div>
                              <span className="text-white/40">FF Number:</span>
                              <p className="text-white/85 font-normal">{passenger.frequent_flyer_number}</p>
                            </div>
                          )}
                          {passenger.known_traveler_number && (
                            <div>
                              <span className="text-white/40">KTN:</span>
                              <p className="text-white/85 font-normal">{passenger.known_traveler_number}</p>
                            </div>
                          )}
                          {(() => {
                            const seatLabel = getSeatPreferenceLabel(passenger.seat_preference || passenger.seatPreference);
                            if (!seatLabel) return null;
                            return (
                              <div>
                                <span className="text-white/40 inline-flex items-center gap-1">
                                  <Armchair className="w-3 h-3" /> Seat:
                                </span>
                                <p className="text-white/85 font-normal">{seatLabel}</p>
                              </div>
                            );
                          })()}
                        </div>
                        {passenger.special_requests && (
                          <div className="pt-2 border-t border-white/[0.06]">
                            <span className="text-white/40 text-xs">Special Requests:</span>
                            <p className="text-white/85 font-normal text-xs mt-1">{passenger.special_requests}</p>
                          </div>
                        )}
                      </div>
                    ))}
              </CartSection>
            )}
          </div>
          
          {/* Price Summary - Right Side */}
          <div className="space-y-6">
            {/* Trip Information Card */}
            <CartSection icon={Calendar} title="Trip Information" delay={0.15}>
                  {/* Booking Reference (from offer ID) */}
                  {cart.flight_data?.id && (
                    <div className="pb-4 border-b border-white/[0.06]">
                      <p className="text-[11px] text-white/40 uppercase tracking-wider mb-1">Booking Reference</p>
                      <p className="text-lg text-white font-mono font-medium tracking-wider">{cart.flight_data.id.slice(-6).toUpperCase()}</p>
                      <p className="text-xs text-white/60 font-normal mt-1">Use this code to track your booking</p>
                    </div>
                  )}
                  
                  {/* Flight Dates */}
                  <div>
                    <p className="text-[11px] text-white/40 uppercase tracking-wider mb-2">Flight Date(s)</p>
                    <div className="space-y-1.5">
                      {cart.flight_data?.slices?.map((slice, index) => {
                        const firstSegment = slice.segments?.[0];
                        const departingDate = firstSegment?.departing_at ? 
                          new Date(firstSegment.departing_at).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          }) : 'N/A';
                        return (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            <Plane className="w-3 h-3 text-brand-300/70" />
                            <span className="text-white/85 font-normal">
                              {index === 0 ? 'Outbound' : 'Return'}: {departingDate}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
            </CartSection>

            {/* Price Summary — values come from `derivePricingForCart`, which
                prefers the pricing snapshot persisted by Review.jsx (i.e. the
                exact data the customer saw at checkout) and falls back to
                `calculateReviewPagePricing` for legacy carts. The
                `pricing.source` field ('snapshot' vs 'recalculated') tells
                admins whether the numbers came from the customer's checkout
                receipt or were reconstructed; we surface that in the
                SectionBand subtitle for transparency. */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <div className={CART_SECTION_CARD}>
                <SectionBand
                  icon={DollarSign}
                  title="Price Summary"
                  subtitle={pricing.source === 'snapshot' ? 'Matches checkout receipt' : 'Reconstructed from cart inputs'}
                />
                <div className="px-6 py-5 space-y-3">
                  {paymentMethod && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/55 font-normal">Payment</span>
                      <span className="text-white/90 font-normal">
                        {paymentMethod === 'miles' && 'Miles Only'}
                        {paymentMethod === 'installments' && 'Installments'}
                        {paymentMethod === 'miles_installments' && 'Miles + Installments'}
                        {paymentMethod === 'reserve' && 'Reserve Credits'}
                        {paymentMethod === 'defera_zero' && 'Defera Zero'}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-white/55 font-normal">Base Fare</span>
                    <span className="text-white/90 font-normal tabular-nums">${basePrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  {hasCredits && (
                    <div className="flex justify-between text-sm">
                      <span className="text-emerald-300 font-normal">Applied Credits</span>
                      <span className="text-emerald-300 font-normal tabular-nums">-${creditsTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {isMilesInstallments && milesUsedDisplay > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-brand-400 font-normal">Miles Used</span>
                      <span className="text-brand-400 font-normal tabular-nums">{milesUsedDisplay.toLocaleString()} miles</span>
                    </div>
                  )}
                  {isMilesInstallments && cashPortionDisplay != null && cashPortionDisplay > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/55 font-normal">Cash Portion (after miles)</span>
                      <span className="text-white/90 font-normal tabular-nums">${cashPortionDisplay.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {stayTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/55 font-normal">Hotel ({stay?.nights || 0} {(stay?.nights || 0) === 1 ? 'night' : 'nights'})</span>
                      <span className="text-white/90 font-normal tabular-nums">${stayTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  {!isMilesOnly && (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/55 font-normal">Service Fee {isFlightHotel || promoApplied ? '' : `(${getServiceFeeDisplayLabel(serviceFee, isMilesInstallments ? cashPortionDisplay : basePriceForFees)})`}</span>
                        <span className={isFlightHotel || promoApplied ? 'text-emerald-300 font-normal' : 'text-white/90 font-normal tabular-nums'}>
                          {isFlightHotel ? 'Waived' : (promoApplied ? '$0.00' : `$${serviceFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)}
                        </span>
                      </div>
                      <div className="border-t border-white/[0.06] pt-2.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-white/55 font-normal">Subtotal</span>
                          <span className="text-white/90 font-normal tabular-nums">${subtotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-white/55 font-normal">Hold Fee</span>
                        <span className="text-white/90 font-normal tabular-nums">${holdFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                      {discountApplied > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/55 font-normal">Discount ({discountCode})</span>
                          <span className="text-emerald-300 font-normal tabular-nums">-${discountApplied.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </div>
                      )}
                    </>
                  )}
                  {seatTotal > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-white/55 font-normal">Seat Selection</span>
                      <span className="text-white/90 font-normal tabular-nums">${seatTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  )}
                  <div className="border-t border-white/[0.08] pt-3 mt-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-white text-sm font-normal">Total</span>
                      <span className="text-[hsl(228,35%,58%)] font-light text-lg tabular-nums">${finalTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Payment Method & Miles Information */}
            {paymentMethod && paymentMethod !== 'installments' && (
              <CartSection icon={DollarSign} title="Payment Details" delay={0.3} bodyClassName="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/55 font-normal">Payment Method</span>
                      <span className="text-white/90 font-medium capitalize">
                        {paymentMethod === 'miles' && 'Miles Only'}
                        {paymentMethod === 'installments' && 'Installments'}
                        {paymentMethod === 'miles_installments' && 'Miles + Installments'}
                        {paymentMethod === 'reserve' && 'Reserve Credits'}
                      </span>
                    </div>
                    {(milesToDeplete > 0 || milesUsedDisplay > 0) && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/55 font-normal">Miles to Deplete</span>
                        <span className="text-brand-300 font-medium tabular-nums">{(milesToDeplete || milesUsedDisplay || 0).toLocaleString()} miles</span>
                      </div>
                    )}
                    {paymentMethod === 'miles' && (
                      <div className="flex items-start gap-3 rounded-xl bg-brand-500/[0.07] border border-brand-500/25 px-3.5 py-3">
                        <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-500/15 ring-1 ring-inset ring-brand-500/30">
                          <Info className="w-3 h-3 text-brand-300" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-brand-50 leading-tight">
                            Miles-Only Checkout
                          </p>
                          <p className="mt-0.5 text-[12px] sm:text-[13px] text-brand-200/75 leading-snug">
                            {ic.editCartMilesOnlyAdmin}
                          </p>
                        </div>
                      </div>
                    )}
                    {paymentMethod === 'miles_installments' && (
                      <div className="flex items-start gap-3 rounded-xl bg-brand-500/[0.07] border border-brand-500/25 px-3.5 py-3">
                        <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-brand-500/15 ring-1 ring-inset ring-brand-500/30">
                          <Info className="w-3 h-3 text-brand-300" strokeWidth={2.5} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-brand-50 leading-tight">
                            Miles + Installments
                          </p>
                          <p className="mt-0.5 text-[12px] sm:text-[13px] text-brand-200/75 leading-snug">
                            {ic.editCartMilesInstallmentsPrefix}{(milesToDeplete || milesUsedDisplay || 0).toLocaleString()}{ic.editCartMilesInstallmentsSuffix}
                          </p>
                        </div>
                      </div>
                    )}
                    {paymentMethod === 'reserve' && (
                      <div className="flex items-start gap-3 rounded-xl bg-emerald-500/[0.07] border border-emerald-500/25 px-3.5 py-3">
                        <div className="mt-0.5 flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15 ring-1 ring-inset ring-emerald-500/30">
                          <Check className="w-3 h-3 text-emerald-300" strokeWidth={2.75} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-emerald-50 leading-tight">
                            Reserve Credits
                          </p>
                          <p className="mt-0.5 text-[12px] sm:text-[13px] text-emerald-200/75 leading-snug">
                            Paid in full from your Defera Reserve balance — nothing else to charge.
                          </p>
                        </div>
                      </div>
                    )}
                    {status === 'ready_for_push' && (user?.id || cart?.user_id) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(createPageUrl(`UserDetails?id=${user?.id || cart?.user_id}`))}
                        className={cn(GHOST_BTN_CLASS, 'w-full h-10')}
                      >
                        <UserIcon className="w-4 h-4 mr-2" />
                        Deplete Miles
                      </Button>
                    )}
              </CartSection>
            )}

            {/* Payment Schedule - Only show if payment_schedule exists in cart.
                NOTE: deliberately DO NOT pass `tripId` here. `cart.id` lives in
                `abandoned_trips`, not `bookings`, so any live Stripe-truth lookup
                (PaymentScheduleDisplay's `defera-capital/schedule?booking_id=…`
                fetch on mount) would 404 with "Defera Capital trip not found"
                and render that as a banner inside the schedule card. Carts have
                no Stripe subscription yet; all tripId-keyed admin actions are
                also hidden via `showActions={false}`. The cart's stored
                `payment_schedule` (deposit_percentage / amount_cents) is shown
                via local computation only. */}
            {cart.flight_data?.payment_schedule && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.32 }}>
                <div className="flex justify-end mb-2">
                  <PaymentProcessorBadge flightData={cart.flight_data} />
                </div>
                <PaymentScheduleDisplay 
                  paymentSchedule={cart.flight_data.payment_schedule}
                  totalWithFee={totalForPaymentSchedule}
                  departureDate={cart.departure_date}
                  tripReference={cart.flight_data?.defera_booking_reference || cart.booking_reference || ''}
                  userName={user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : (cart.user_email || 'Customer')}
                  route={cart.departure_city && cart.arrival_city ? `${cart.departure_city} → ${cart.arrival_city}` : ''}
                  showActions={false}
                  isDeferaCapital={isDeferaCapitalEffective(cart.payment_method || cart.flight_data?._payment_method || cart.flight_data?.payment_method)}
                />
              </motion.div>
            )}

            {/* Admin Controls */}
            <CartSection icon={Save} title="Admin Controls" delay={0.35}>
                  <div className="space-y-2">
                    <label className="text-[11px] text-white/40 uppercase tracking-wider">Status</label>
                    <Select value={status} onValueChange={setStatus} disabled={isProcessing}>
                      <SelectTrigger className="bg-brand-900/25 text-white border-brand-800/30 rounded-xl hover:border-brand-700/40 transition-colors">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_cart">In Cart</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="canceled">Canceled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleStatusChange} disabled={isProcessing || status === cart.status} className={cn(BRAND_PRIMARY_BTN_CLASS, 'w-full h-10')}>
                    {isProcessing ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-2"/> Update Status</>}
                  </Button>
            </CartSection>

            {/* Payment Link */}
            {cart.payment_link && (
              <CartSection icon={ExternalLink} title={ic.externalPaymentLinkLabel} subtitle="Customer payment installment plan" delay={0.4} bodyClassName="space-y-3">
                    <Button
                      onClick={() => window.open(cart.payment_link, '_blank')}
                      className={cn(BRAND_PRIMARY_BTN_CLASS, 'w-full h-10')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Open Payment Link
                    </Button>
                    <div className="p-2.5 bg-black/30 border border-white/[0.06] rounded-xl">
                      <p className="text-xs text-white/50 font-mono break-all">{cart.payment_link}</p>
                    </div>
              </CartSection>
            )}

            {(status === 'confirmed' || status === 'ready_for_push') && (
              <CartSection icon={Send} title="Convert to Trip" subtitle="Create a confirmed booking in Trips" delay={0.45}>
                     {/* Backer Selection */}
                     <div>
                       <label className="text-[11px] text-white/40 uppercase tracking-wider mb-2 block">
                         Who is backing this trip?
                       </label>
                       <Select value={selectedBackerId} onValueChange={setSelectedBackerId}>
                         <SelectTrigger className="bg-brand-900/25 border-brand-800/30 text-white rounded-xl hover:border-brand-700/40 transition-colors">
                           <SelectValue placeholder="Select backer" />
                         </SelectTrigger>
                         <SelectContent>
                           <SelectItem value="none">None</SelectItem>
                           {backers.map(backer => (
                             <SelectItem key={backer.id} value={backer.id}>
                               {backer.firstName && backer.lastName 
                                 ? `${backer.firstName} ${backer.lastName} (${backer.email})`
                                 : backer.email}
                             </SelectItem>
                           ))}
                         </SelectContent>
                       </Select>
                     </div>
                     <Button onClick={handlePushToTrips} disabled={isProcessing} className={cn(BRAND_PRIMARY_BTN_CLASS, 'w-full h-10')}>
                       {isProcessing ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Send className="w-4 h-4 mr-2"/> Push to Trips</>}
                     </Button>
              </CartSection>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}